import { CSSProperties } from "react";
import {
  HorizontalPosition,
  SocialIcon,
  TemplateElement,
} from "~/entities/baseEntities";
import { Shape, Size } from "~/entities/templateEntities";

export enum Screen {
  Desktop = "Desktop",
  Mobile = "Mobile",
}

export const NavBarSize: Record<Size, string> = {
  [Size.S]: "60px",
  [Size.M]: "80px",
  [Size.L]: "100px",
};

export const MenuBarSideListFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const MenuBarTopListFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const MenuBarTopTitleFontSize: Record<Size, string> = {
  [Size.S]: "18px",
  [Size.M]: "20px",
  [Size.L]: "22px",
};

export const MenuBarTopDescriptionFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const InfoBarPointsSize: Record<Size, string> = {
  [Size.S]: "24px",
  [Size.M]: "28px",
  [Size.L]: "32px",
};

export const LocationPickerFieldFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const getButtonStyles = (
  color: string,
  borderColor: string,
  fontColor: string,
  size: Size,
  style: Shape,
): CSSProperties => {
  const styles: CSSProperties = {
    backgroundColor: color,
    borderColor,
    color: fontColor,
  };

  if (size === Size.S) {
    styles.height = 46;
  } else if (size === Size.M) {
    styles.height = 52;
  } else if (size === Size.L) {
    styles.height = 58;
  }

  if (style === Shape.Box) {
    styles.borderRadius = 0;
  } else if (style === Shape.RoundedBox) {
    styles.borderRadius = 8;
  } else if (style === Shape.FullyRoundedBox) {
    styles.borderRadius = +(styles.height ?? 0) / 2;
  }

  return styles;
};

export const StoresTitleFontSize: Record<Size, string> = {
  [Size.S]: "20px",
  [Size.M]: "24px",
  [Size.L]: "28px",
};

export const StoreCartNameFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const StoreCartCityFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const StoreCartOtherFontSize: Record<Size, string> = {
  [Size.S]: "12px",
  [Size.M]: "14px",
  [Size.L]: "16px",
};

export const MenuTabFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const MenuTabBorderRadius: Record<Shape, string> = {
  [Shape.Circle]: "50%",
  [Shape.Box]: "0px",
  [Shape.RoundedBox]: "6px",
  [Shape.FullyRoundedBox]: "21px",
};

export const MenuTabImageBorderRadius: Record<Shape, string> = {
  [Shape.Circle]: "50%",
  [Shape.Box]: "0px",
  [Shape.RoundedBox]: "6px",
  [Shape.FullyRoundedBox]: "21px",
};

export const CategoryFontSize: Record<Size, string> = {
  [Size.S]: "32px",
  [Size.M]: "40px",
  [Size.L]: "48px",
};

export const ProductCardTitleFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const ProductCardDescriptionFontSize: Record<Size, string> = {
  [Size.S]: "12px",
  [Size.M]: "14px",
  [Size.L]: "16px",
};

export const ProductCardPriceFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "20px",
};

export const ProductCardImageSize: Record<Size, string> = {
  [Size.S]: "100px",
  [Size.M]: "110px",
  [Size.L]: "120px",
};

export const ProductCardImageBorderRadius: Record<Shape, string> = {
  [Shape.Circle]: "0px",
  [Shape.Box]: "0px",
  [Shape.RoundedBox]: "12px",
  [Shape.FullyRoundedBox]: "0px",
};

export const CartItemNameFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const CartItemModifiersFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const CartItemPriceFontSize: Record<Size, string> = {
  [Size.S]: "18px",
  [Size.M]: "20px",
  [Size.L]: "22px",
};

export const ProductModalInfoTitleFontSize: Record<Size, string> = {
  [Size.S]: "24px",
  [Size.M]: "28px",
  [Size.L]: "32px",
};

export const ProductModalInfoDescriptionFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const ProductModalInfoPriceFontSize: Record<Size, string> = {
  [Size.S]: "20px",
  [Size.M]: "24px",
  [Size.L]: "28px",
};

export const ProductModalInfoAllergensFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const ProductModalModifierHeaderFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const ProductModalModifierFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "16px",
  [Size.L]: "18px",
};

export const TemplateElementHorizontalPosition: Record<
  HorizontalPosition,
  CSSProperties["alignSelf"]
> = {
  [HorizontalPosition.Left]: "self-start",
  [HorizontalPosition.Center]: "center",
  [HorizontalPosition.Right]: "self-end",
};

export const TemplateElementButtonHorizontalPosition: Record<
  HorizontalPosition,
  CSSProperties["justifyContent"]
> = {
  [HorizontalPosition.Left]: "flex-start",
  [HorizontalPosition.Center]: "center",
  [HorizontalPosition.Right]: "flex-end",
};

export const TemplateElementInRowHorizontalPosition: Record<
  HorizontalPosition,
  CSSProperties["justifySelf"]
> = {
  [HorizontalPosition.Left]: "start",
  [HorizontalPosition.Center]: "center",
  [HorizontalPosition.Right]: "end",
};

export const LoyaltyDialogTitleFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "28px",
  [Size.L]: "40px",
};

export const LoyaltyDialogSubtitleFontSize: Record<Size, string> = {
  [Size.S]: "14px",
  [Size.M]: "18px",
  [Size.L]: "22px",
};

export const LoyaltyDialogCardTitleFontSize: Record<Size, string> = {
  [Size.S]: "16px",
  [Size.M]: "18px",
  [Size.L]: "20px",
};

export const LoyaltyDialogCardDescriptionFontSize: Record<Size, string> = {
  [Size.S]: "12px",
  [Size.M]: "14px",
  [Size.L]: "16px",
};

export const ElementOptions = [
  {
    name: "Link",
    value: TemplateElement.Link,
  },
  {
    name: "Styled Link",
    value: TemplateElement.StyledLink,
  },
  {
    name: "Button",
    value: TemplateElement.Button,
  },
  {
    name: "Text",
    value: TemplateElement.Text,
  },
  {
    name: "Socials",
    value: TemplateElement.Socials,
  },
  {
    name: "Copyright",
    value: TemplateElement.Copyright,
  },
  {
    name: "Row",
    value: TemplateElement.Row,
  },
  {
    name: "Image",
    value: TemplateElement.Image,
  },
];

export const SocialIconOptions = [
  {
    name: "Instagram",
    value: SocialIcon.Instagram,
  },
  {
    name: "Facebook",
    value: SocialIcon.Facebook,
  },
  {
    name: "Twitter",
    value: SocialIcon.Twitter,
  },
  {
    name: "Pinterest",
    value: SocialIcon.Pinterest,
  },
  {
    name: "Tiktok",
    value: SocialIcon.Tiktok,
  },
  {
    name: "Youtube",
    value: SocialIcon.Youtube,
  },
  {
    name: "Yelp",
    value: SocialIcon.Yelp,
  },
  {
    name: "Google",
    value: SocialIcon.Google,
  },
  {
    name: "Discord",
    value: SocialIcon.Discord,
  },
  {
    name: "Linkedin",
    value: SocialIcon.Linkedin,
  },
  {
    name: "Intercom",
    value: SocialIcon.Intercom,
  },
  {
    name: "Apple",
    value: SocialIcon.Apple,
  },
  {
    name: "Android",
    value: SocialIcon.Android,
  },
  {
    name: "Generic",
    value: SocialIcon.Generic,
  },
];
